/**
 * firstVisitAttribution
 *
 * Captura el referrer + URL del PRIMER pageview de la session y los persiste
 * en sessionStorage. Si el visitor navega internamente (SPA-nav, links a
 * /vip → /checkout → /pagar), los UTM y el referrer original se "pierden"
 * sin esta capa porque cada nav SPA pisa document.referrer con el host propio.
 *
 * Para growth attribution necesitamos saber:
 *   - De dónde vino al sitio (referrer del primer pageview)
 *   - Por qué landing aterrizó (URL completa con UTMs)
 *
 * DataFast (script.js) lee del landing los mismos params y los atribuye al
 * visitor automáticamente — no hace falta reenviarlos a DataFast. Esta capa
 * es para Slack / Stripe / PostHog internos. Params que DataFast recoge:
 *   ref, source, via, utm_source, utm_medium, utm_campaign, utm_term, utm_content
 * Docs: https://datafa.st/docs/utm-tracking
 *
 * Use:
 *   import { getFirstVisitAttribution, recordFirstVisitAttribution } from "@/lib/firstVisitAttribution";
 *
 *   // En el AppShell, una vez al boot:
 *   recordFirstVisitAttribution();
 *
 *   // En cualquier checkout/track endpoint POST:
 *   body: JSON.stringify({
 *     ...payload,
 *     ...getFirstVisitAttribution(),  // → { referrer, landingUrl }
 *   })
 */

const STORAGE_KEY = "juaperrone.first_visit_attribution.v1";

/** Params that DataFast (and this layer) treat as campaign / traffic source. */
export const DATAFAST_TRACKING_PARAM_KEYS = [
  "ref",
  "source",
  "via",
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
] as const;

export type DataFastTrackingParamKey = (typeof DATAFAST_TRACKING_PARAM_KEYS)[number];

export type DataFastTrackingParams = Partial<
  Record<DataFastTrackingParamKey, string>
>;

interface FirstVisitAttribution {
  /** document.referrer del primer pageview de la session. "" si direct. */
  referrer: string;
  /** window.location.href del primer pageview (con todos los UTM crudos). */
  landingUrl: string;
  /** ISO timestamp de cuándo se capturó. Útil para audits "cuánto tarda el funnel". */
  capturedAt: string;
  /** Params de campaña del primer pageview (subset DataFast). */
  trackingParams?: DataFastTrackingParams;
}

/** Parse DataFast-compatible tracking params from a URL or the current location. */
export function parseDataFastTrackingParams(
  href?: string,
): DataFastTrackingParams {
  if (typeof window === "undefined" && !href) return {};
  try {
    const url = new URL(href || window.location.href);
    const out: DataFastTrackingParams = {};
    for (const key of DATAFAST_TRACKING_PARAM_KEYS) {
      const raw = url.searchParams.get(key);
      if (raw == null) continue;
      const v = raw.trim().slice(0, 200);
      if (v) out[key] = v;
    }
    return out;
  } catch {
    return {};
  }
}

/**
 * Build a juaperrone.com (or absolute) campaign URL with DataFast-friendly params.
 * Example: buildCampaignUrl("/", { via: "instagram_bio", utm_source: "instagram", utm_medium: "social" })
 */
export function buildCampaignUrl(
  pathOrUrl: string,
  params: DataFastTrackingParams,
  baseOrigin?: string,
): string {
  const origin =
    baseOrigin ||
    (typeof window !== "undefined" ? window.location.origin : "https://juaperrone.com");
  let url: URL;
  try {
    url = pathOrUrl.startsWith("http")
      ? new URL(pathOrUrl)
      : new URL(pathOrUrl.startsWith("/") ? pathOrUrl : `/${pathOrUrl}`, origin);
  } catch {
    url = new URL("/", origin);
  }
  for (const [k, v] of Object.entries(params)) {
    if (v == null || String(v).trim() === "") continue;
    if (!(DATAFAST_TRACKING_PARAM_KEYS as readonly string[]).includes(k)) continue;
    url.searchParams.set(k, String(v).trim().slice(0, 200));
  }
  return url.toString();
}

/**
 * Captura referrer + landingUrl si no se capturó todavía en esta session.
 * Llamar lo más temprano posible en el ciclo de vida del cliente (App boot).
 * Idempotente — sucesivas llamadas no pisan la primera (queremos la verdadera
 * "first visit", no la URL actual después de navegación SPA).
 */
export function recordFirstVisitAttribution(): void {
  if (typeof window === "undefined") return;
  try {
    const existing = window.sessionStorage.getItem(STORAGE_KEY);
    if (existing) return; // ya capturada → no pisar
    let rawReferrer = typeof document !== "undefined" ? document.referrer || "" : "";
  // Normalize Instagram wrapper (l.instagram.com) for better attribution in analytics
  if (rawReferrer.includes("l.instagram.com") || rawReferrer.includes("instagram.com")) {
    rawReferrer = "instagram";
  }
  const landingUrl = window.location.href;
  const payload: FirstVisitAttribution = {
      referrer: rawReferrer,
      landingUrl,
      capturedAt: new Date().toISOString(),
      trackingParams: parseDataFastTrackingParams(landingUrl),
    };
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch {
    /* sessionStorage puede fallar en private mode — silent fail, no rompemos UX */
  }
}

/**
 * Devuelve { referrer, landingUrl } listos para spreadear en un body POST.
 * Si nunca se capturó nada (private mode, ssr, primer boot raro), devuelve
 * el state actual como fallback. Slack alerts tienen handling de strings
 * vacíos así que cualquier output es seguro.
 */
export function getFirstVisitAttribution(): {
  referrer: string;
  landingUrl: string;
  trackingParams: DataFastTrackingParams;
} {
  if (typeof window === "undefined") {
    return { referrer: "", landingUrl: "", trackingParams: {} };
  }
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as FirstVisitAttribution;
      const landingUrl = parsed.landingUrl ?? "";
      const trackingParams =
        parsed.trackingParams && Object.keys(parsed.trackingParams).length > 0
          ? parsed.trackingParams
          : parseDataFastTrackingParams(landingUrl);
      return {
        referrer: parsed.referrer ?? "",
        landingUrl,
        trackingParams,
      };
    }
  } catch {
    /* fallback abajo */
  }
  // Fallback: state actual (mejor que strings vacíos cuando sessionStorage
  // no está disponible — al menos capturamos lo que vemos AHORA).
  let fbReferrer = typeof document !== "undefined" ? document.referrer || "" : "";
  if (fbReferrer.includes("l.instagram.com") || fbReferrer.includes("instagram.com")) {
    fbReferrer = "instagram";
  }
  const landingUrl = window.location.href;
  return {
    referrer: fbReferrer,
    landingUrl,
    trackingParams: parseDataFastTrackingParams(landingUrl),
  };
}

/** First-touch campaign params only (for Stripe metadata / analytics spreads). */
export function getFirstVisitTrackingParams(): DataFastTrackingParams {
  return getFirstVisitAttribution().trackingParams;
}

/**
 * Helper para forzar reset (testing / debug). NO usar en prod —
 * la idea es que cada session tenga UNA atribución fija.
 */
export function resetFirstVisitAttribution(): void {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
}
